// lib
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Transition } from 'react-transition-group'

import { DOMchildOf } from '@aytm/lib/common/func'

import useEventListener from '@aytm/app/stats/hooks/useEventListener'

// components
import { RadioButton } from 'primereact/radiobutton'
import { Slider } from 'primereact/slider'
import { Dropdown } from 'primereact/dropdown'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const BUTTON_TEXTS = {
  turf: 'TURF',
  emd: 'Impression Dial',
  concept_lab: 'Concept Lab',
  concept_mapping: 'Concept Mapping',
  tracker_lab: 'Tracker Lab',
  tracker_mapping: 'Tracker Mapping'
}

const THRESHOLD_OPTIONS = [
  { key: '0.2', text: '20%' },
  { key: '0.25', text: '25%' },
  { key: '0.333', text: '33.3%' },
  { key: '0.5', text: '50%' },
  { key: '0.6', text: '60%' },
  { key: '0.7', text: '70%' },
  { key: '0.8', text: '80%' }
]
const DEFAULT_THRESHOLD_OPTION = '0.7'

const DEFAULT_ITEMS_PER_SET = 4

const ExportSpecificButton = ({ item, processing, disabled, onExport }) => {
  const onClick = useCallback(() => {
    'function' == typeof onExport && onExport({ method: item.key, methodText: item.xlsxText, ...item.exportOptions })
  }, [item, onExport])

  return (
    <div className={`${style.row} p-fluid`}>
      <div
        className={`${style.button}${processing || disabled ? ' p-disabled' : ''}`}
        onClick={processing || disabled ? null : onClick}
      >
        <span className={style['button-text']}>{BUTTON_TEXTS[item.key]}</span>
        <span className={style['button-handle']}>
          <i className={processing ? 'icon-aytmfd-refresh-v2 aytmfd-spin' : 'icon-aytmfd-export-v2'} />
        </span>
      </div>
    </div>
  )
}

ExportSpecificButton.propTypes = {
  item: PropTypes.object,
  processing: PropTypes.bool,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const ExportSpecificMdOptions = ({ root, model, question, options, processing, disabled, onExport }) => {
  const [collapsed, setCollapsed] = useState(true)

  const [method, setMethod] = useState(model.turf.default)
  const [threshold, setThreshold] = useState(null)

  const gearRef = useRef(null)
  const panelRef = useRef(null)

  const thresholdRef = useRef(null)
  thresholdRef.current = threshold

  const methodObj = useMemo(() => options.find(option => option.key == method) || null, [method, options])

  const hideOnGlobalClick = event => {
    if (
      (!gearRef.current || !DOMchildOf(event.target, gearRef.current)) &&
      (!panelRef.current || !DOMchildOf(event.target, panelRef.current))
    ) {
      setCollapsed(true)
    }
  }

  const onClick = useCallback(
    event => {
      if (
        (!gearRef.current || !DOMchildOf(event.target, gearRef.current)) &&
        (!panelRef.current || !DOMchildOf(event.target, panelRef.current))
      ) {
        setCollapsed(true)
        'function' == typeof onExport &&
          onExport({ method, methodText: methodObj.xlsxText, threshold, ...methodObj.exportOptions })
      }
    },
    [methodObj, method, threshold, onExport]
  )

  useEffect(() => {
    collapsed
      ? document.removeEventListener('click', hideOnGlobalClick, true)
      : document.addEventListener('click', hideOnGlobalClick, true)

    return () => {
      document.removeEventListener('click', hideOnGlobalClick, true)
    }
  }, [collapsed])

  useEffect(
    () =>
      methodObj.threshold
        ? setThreshold(null === thresholdRef.current ? DEFAULT_THRESHOLD_OPTION : thresholdRef.current)
        : setThreshold(null),
    [methodObj]
  )

  useEffect(() => setCollapsed(true), [question.key, processing])

  return (
    <>
      <div className={`${style.row} p-fluid`}>
        <div
          className={`${style.button}${processing || disabled ? ' p-disabled' : ''}`}
          onClick={processing || disabled ? null : onClick}
        >
          <span className={style['button-text']}>{null !== methodObj ? methodObj.text : '&nbsp;'}</span>
          <span className={style['button-handle']}>
            <i className={processing ? 'icon-aytmfd-refresh-v2 aytmfd-spin' : 'icon-aytmfd-export-v2'} />
          </span>
          <Transition in={!collapsed} timeout={500}>
            {status => (
              <div
                ref={panelRef}
                className={`${style['button-panel']} ${style['transition-panel']}${
                  status ? ' ' + style['transition-panel-' + status] : ''
                }`}
              >
                <div className={style['button-panel-wrapper']}>
                  <ul className={style['button-panel-items']}>
                    {options.map((item, index) => (
                      <li key={`${item.key}-${index}`} className={style['button-panel-item']}>
                        <RadioButton
                          value={item.key}
                          checked={method == item.key}
                          disabled={processing || disabled}
                          onChange={processing || disabled ? null : () => setMethod(item.key)}
                        />
                        <span
                          className={classNames(style['button-panel-item-label'], {
                            'p-disabled': processing || disabled
                          })}
                          onClick={processing || disabled ? null : () => setMethod(item.key)}
                        >
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </Transition>
        </div>
        <div
          ref={gearRef}
          className={`${style['button-config']}${!collapsed ? ' ' + style['button-config-active'] : ''}${
            processing || disabled ? ' p-disabled' : ''
          }`}
          onClick={processing || disabled ? null : () => setCollapsed(!collapsed)}
        >
          <i className="icon-aytmfd-config"></i>
        </div>
        {model.turf.help ? (
          <SidebarPopup
            id="export"
            root={root}
            help={model.turf.help.replace('{{itemsPerSet}}', question.items_per_set || DEFAULT_ITEMS_PER_SET)}
            width="300px"
          />
        ) : null}
      </div>
      {methodObj.threshold && null !== threshold ? (
        <div className={`${style.row} ${style['row-slider']} p-fluid`}>
          <div className="p-field p-col-12">
            <Slider
              min={0}
              max={THRESHOLD_OPTIONS.length - 1}
              value={THRESHOLD_OPTIONS.findIndex(value => value.key == threshold) || 0}
              labelTemplate={value => THRESHOLD_OPTIONS[value].text}
              disabled={processing || disabled}
              onChange={processing || disabled ? null : event => setThreshold(THRESHOLD_OPTIONS[event.value].key)}
            />
          </div>
        </div>
      ) : null}
    </>
  )
}

ExportSpecificMdOptions.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  question: PropTypes.object,
  options: PropTypes.array,
  processing: PropTypes.bool,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const ExportSpecificTurfOptions = ({ question, options, processing, disabled, onExport }) => {
  const [collapsed, setCollapsed] = useState(true)

  const gearRef = useRef(null)
  const panelRef = useRef(null)

  const hideOnGlobalClick = event => {
    if (
      (!gearRef.current || !DOMchildOf(event.target, gearRef.current)) &&
      (!panelRef.current || !DOMchildOf(event.target, panelRef.current))
    ) {
      setCollapsed(true)
    }
  }

  const onClick = useCallback(
    item => {
      setCollapsed(true)
      'function' == typeof onExport && onExport({ method: item.key, methodText: item.xlsxText, ...item.exportOptions })
    },
    [onExport]
  )

  useEffect(() => {
    collapsed
      ? document.removeEventListener('click', hideOnGlobalClick, true)
      : document.addEventListener('click', hideOnGlobalClick, true)

    return () => {
      document.removeEventListener('click', hideOnGlobalClick, true)
    }
  }, [collapsed])

  useEffect(() => setCollapsed(true), [question.key, processing])

  return (
    <div className={`${style.row} p-fluid`}>
      <div
        className={`${style.button}${processing || disabled ? ' p-disabled' : ''}`}
        onClick={processing || disabled ? null : () => setCollapsed(!collapsed)}
      >
        <span className={style['button-text']}>Select available options</span>
        <span ref={gearRef} className={style['button-handle']}>
          <i className={processing ? 'icon-aytmfd-refresh-v2 aytmfd-spin' : 'icon-aytmfd-download-combobox'} />
        </span>
        <Transition in={!collapsed} timeout={500}>
          {status => (
            <div
              ref={panelRef}
              className={`${style['button-panel']} ${style['transition-panel']}${
                status ? ' ' + style['transition-panel-' + status] : ''
              }`}
            >
              <div className={style['button-panel-wrapper']}>
                <ul className={style['button-panel-items']}>
                  {options.map((item, index) => (
                    <li
                      key={`${item.key}-${index}`}
                      className={style['button-panel-item']}
                      onClick={processing || disabled ? null : () => onClick(item)}
                    >
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Transition>
      </div>
    </div>
  )
}

ExportSpecificTurfOptions.propTypes = {
  question: PropTypes.object,
  options: PropTypes.array,
  processing: PropTypes.bool,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

const ExportSpecific = ({ root, model, data, features, disabled, onExport }) => {
  const [processing, setProcessing] = useState(false)

  const [exportType, setExportType] = useState(null)
  const [exportQuestion, setExportQuestion] = useState(null)

  const exportTypeRef = useRef(null)
  exportTypeRef.current = exportType

  const exportQuestionRef = useRef(null)
  exportQuestionRef.current = exportQuestion

  const isTurfEnabled = useMemo(
    () => 0 < data.turf.length && !features.turf.disabled && !disabled,
    [data.turf, features.turf.disabled, disabled]
  )
  const isCjExportEnabled = useMemo(() => 0 < data.conjoint.length && !disabled, [data.conjoint, disabled])
  const isEmdExportEnabled = useMemo(
    () => 0 < data.emd.length && !features.authorized_exports.disabled && !disabled,
    [data.emd, features.authorized_exports.disabled, disabled]
  )
  const isCLExportEnabled = useMemo(
    () => 0 < data.concept_lab.length && !features.authorized_exports.disabled && !disabled,
    [data.concept_lab, features.authorized_exports.disabled, disabled]
  )
  const isCLMappingExportEnabled = useMemo(
    () => 0 < data.concept_mapping.length && !features.authorized_exports.disabled && !disabled,
    [data.concept_mapping, features.authorized_exports.disabled, disabled]
  )
  const isTLExportEnabled = useMemo(
    () => 0 < data.tracker_lab.length && !features.authorized_exports.disabled && !disabled,
    [data.tracker_lab, features.authorized_exports.disabled, disabled]
  )
  const isTLMappingExportEnabled = useMemo(
    () => 0 < data.tracker_mapping.length && !features.authorized_exports.disabled && !disabled,
    [data.tracker_mapping, features.authorized_exports.disabled, disabled]
  )
  const isECEnabled = useMemo(
    () =>
      data.ec &&
      0 < data.ec.length &&
      !features.authorized_exports.disabled &&
      !disabled &&
      features.ec_export.available,
    [data.ec, features.authorized_exports.disabled, disabled, features.ec_export.available]
  )

  const exportTypeOptions = useMemo(() => {
    const options = []
    isTurfEnabled && options.push({ text: 'TURF ANALYSES', key: 'turf', exportType: 'turf' })
    isCjExportEnabled && options.push({ text: 'CONJOINT ANALYSES', key: 'conjoint', exportType: 'conjoint' })
    isEmdExportEnabled && options.push({ text: 'IMPRESSION DIAL ANALYSES', key: 'emd', exportType: 'emd' })
    isCLExportEnabled && options.push({ text: 'CONCEPT LAB', key: 'concept_lab', exportType: 'concept_lab' })
    isCLMappingExportEnabled &&
      options.push({ text: 'CONCEPT MAPPING', key: 'concept_mapping', exportType: 'concept_lab' })
    isTLExportEnabled && options.push({ text: 'TRACKER LAB', key: 'tracker_lab', exportType: 'tracker_lab' })
    isTLMappingExportEnabled &&
      options.push({ text: 'TRACKER MAPPING', key: 'tracker_mapping', exportType: 'tracker_lab' })
    isECEnabled && options.push({ text: 'ECOMMERCE EVENTS', key: 'ec', exportType: 'ec' })
    return options
  }, [
    isTurfEnabled,
    isCjExportEnabled,
    isEmdExportEnabled,
    isCLExportEnabled,
    isCLMappingExportEnabled,
    isTLExportEnabled,
    isTLMappingExportEnabled,
    isECEnabled
  ])

  const defaultExportType = useMemo(
    () => (0 < exportTypeOptions.length ? exportTypeOptions[0].key : null),
    [exportTypeOptions]
  )

  const exportTypeObj = useMemo(() => {
    const option = exportTypeOptions.find(option => option.key == exportType)
    if (option == undefined) return null

    return option
  }, [exportType, exportTypeOptions])

  const exportQuestionsOptions = useMemo(() => data[exportType] || [], [data, exportType])

  const exportQuestionObj = useMemo(() => {
    const option = exportQuestionsOptions.find(option => option.key == exportQuestion)
    if (option == undefined) return null

    return option
  }, [exportQuestion, exportQuestionsOptions])

  const exportQuestionOptions = useMemo(() => {
    if (null === exportQuestionObj) return null

    const exportOptionsDefaults = {
      key: exportQuestionObj.key,
      link: exportQuestionObj.link || null,
      position: exportQuestionObj.position
    }

    if (['cb', 'emd', 'cl', 'tl'].includes(exportQuestionObj.type)) {
      if (null === exportTypeObj) return null

      return {
        key: exportTypeObj.key,
        exportOptions: {
          ...exportOptionsDefaults,
          ...('cb' == exportQuestionObj.type
            ? {
                fileType: 'binary',
                exportType: 'turf',
                turfType: 'answers'
              }
            : {
                fileType: 'emd' == exportQuestionObj.type ? 'csv' : 'xlsx',
                exportType: exportTypeObj.exportType
              })
        }
      }
    }

    if ('ec' == exportQuestionObj.type && features.ec_export.available) {
      return [
        {
          key: 'json',
          text: 'Raw Events',
          exportOptions: {
            ...exportOptionsDefaults,
            fileType: 'json',
            exportType: 'question',
            collect: ['logs'],
            position: exportQuestionObj.position
          }
        }
      ]
    }

    if ('cjc' == exportQuestionObj.type) {
      const exportOptions = {
        ...exportOptionsDefaults,
        fileType: 'csv'
      }
      return [
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          exportOptions: { exportType: 'conjointPkgs', ...exportOptions }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          exportOptions: { exportType: 'conjointDesign', ...exportOptions }
        }
      ]
    }

    if ('cjd' == exportQuestionObj.type) {
      const exportOptions = {
        ...exportOptionsDefaults,
        fileType: 'csv'
      }
      return [
        {
          key: 'conjoint',
          text: 'Raw Utility Scores',
          exportOptions: { exportType: 'conjoint', ...exportOptions }
        },
        {
          key: 'conjointPkgs',
          text: 'Ranked List of Packages',
          exportOptions: { exportType: 'conjointPkgs', ...exportOptions }
        },
        {
          key: 'conjointDesign',
          text: 'Design Sheet by aytm',
          exportOptions: { exportType: 'conjointDesign', ...exportOptions }
        }
      ]
    }

    if ('md' == exportQuestionObj.type) {
      const exportOptions = {
        ...exportOptionsDefaults,
        exportType: 'turf'
      }
      return Object.entries(model.turf.available).map(([key, value]) => {
        return {
          key,
          text: value.text.replace('{{itemsPerSet}}', exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET),
          xlsxText: value.xlsxText.replace('{{itemsPerSet}}', exportQuestionObj.items_per_set || DEFAULT_ITEMS_PER_SET),
          threshold: value.threshold || false,
          exportOptions: { fileType: value.fileType, ...exportOptions }
        }
      })
    }

    const options = []
    const exportOptions = {
      ...exportOptionsDefaults,
      fileType: 'binary',
      exportType: 'turf',
      turfType: 'batches'
    }

    const answerVariants = exportQuestionObj.batches[0].answerVariants.filter(
      av => !Object.hasOwn(av, 'positive') || av.positive
    )

    options.push({
      key: 'bottom1',
      text: `TURF TOP 1 box (A${answerVariants.length})`,
      exportOptions: { method: 'top1', positive: [answerVariants.length], ...exportOptions }
    })
    if (3 < answerVariants.length) {
      options.push({
        key: 'bottom2',
        text: `TURF TOP 2 box (A${answerVariants.length - 1} & A${answerVariants.length})`,
        exportOptions: {
          method: 'top2',
          positive: [answerVariants.length - 1, answerVariants.length],
          ...exportOptions
        }
      })
      options.push({
        key: 'top2',
        text: `TURF TOP 2 box (A1 & A2)`,
        exportOptions: { method: 'top2', positive: [1, 2], ...exportOptions }
      })
    }
    options.push({
      key: 'top1',
      text: `TURF TOP 1 box (A1)`,
      exportOptions: { method: 'top1', positive: [1], ...exportOptions }
    })

    return options
  }, [model.turf, exportTypeObj, exportQuestionObj, features.ec_export.available])

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className={option.icon ? 'p-item-label' : ''}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: `${option.key.toUpperCase()}. ${option.text}` }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const onExportClick = params => {
    setProcessing(true)

    'function' == typeof onExport && onExport(params)
  }

  useEffect(
    () =>
      setExportType(
        exportTypeOptions.some(option => option.key == exportTypeRef.current)
          ? exportTypeRef.current
          : defaultExportType
      ),
    [exportTypeOptions, defaultExportType]
  )

  useEffect(() => setExportQuestion(null), [exportType])

  useEventListener('exportSpecificStopped', () => setProcessing(false))

  return 0 < exportTypeOptions.length ? (
    <div className={style.component}>
      {1 < exportTypeOptions.length ? (
        <>
          <div className={style['component-title']}>Research Analyses</div>
          <div className={`${style.row} p-fluid`}>
            <div className="p-field p-col-12">
              <Dropdown
                value={exportType}
                options={exportTypeOptions}
                optionValue="key"
                optionLabel="text"
                filter={false}
                fixedDropdownDir="bottom"
                disabled={processing || disabled}
                onChange={processing || disabled ? null : event => setExportType(event.value)}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={style['component-title']}>{exportTypeOptions[0].text}</div>
      )}

      {0 < exportQuestionsOptions.length ? (
        'turf' == exportType && features.turf.available ? (
          <Popup
            targetClassName="p-field p-col-12"
            width="254px"
            trigger="hover"
            direction="topleft"
            popupOffset={14}
            content={() => (
              <div>
                <p>
                  {/* PME2 (AYTM-11321): render server-authored `text` verbatim; the icon is absent
                      on the already-on-Enterprise Contact-Us variant, so guard it. */}
                  {features.turf.onPackage.icon ? (
                    <span className={`stat-upgrade-icon stat-upgrade-icon-${features.turf.onPackage.id}`}>
                      <i className={`icon-aytmfd-${features.turf.onPackage.icon}`}></i>
                    </span>
                  ) : null}
                  <span dangerouslySetInnerHTML={{ __html: features.turf.onPackage.text }}></span>
                </p>
              </div>
            )}
          >
            <a className={style.button} href={features.turf.onPackage.upgradeURL} rel="noreferrer">
              <span className={style['button-text']}>Select a question</span>
              <span className={style['button-lock-icon']}>
                <i className="icon-aytmfd-lock"></i>
              </span>
            </a>
          </Popup>
        ) : 'turf' == exportType && features.turf.locked ? (
          <Popup
            targetClassName="p-field p-col-12"
            width="254px"
            trigger="hover"
            direction="topleft"
            popupOffset={14}
            content={() => (
              <div>
                <p>
                  PLEASE LOGIN/JOIN THE TEAM TO UNLOCK ALL FEATURES <br />
                  OF THE STAT REPORT
                </p>
              </div>
            )}
          >
            <div className={style.button}>
              <span className={style['button-text']}>Select a question</span>
              <span className={style['button-lock-icon']}>
                <i className="icon-aytmfd-lock"></i>
              </span>
            </div>
          </Popup>
        ) : (
          <div className={`${style.row} p-fluid`}>
            <div className="p-field p-col-12">
              <Dropdown
                placeholder="Select a question"
                value={exportQuestion}
                options={exportQuestionsOptions}
                optionValue="key"
                optionLabel="text"
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                filter={true}
                filterBy="search,text"
                filterPlaceholder="Search items"
                resetFilterOnHide={true}
                filterInputAutoFocus={false}
                fixedDropdownDir="bottom"
                disabled={processing || disabled}
                onChange={processing || disabled ? null : event => setExportQuestion(event.value)}
              />
            </div>
          </div>
        )
      ) : null}

      {null !== exportQuestionObj ? (
        ['cb', 'emd', 'cl', 'tl'].includes(exportQuestionObj.type) ? (
          <ExportSpecificButton
            question={exportQuestionObj}
            item={exportQuestionOptions}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        ) : 'md' == exportQuestionObj.type ? (
          <ExportSpecificMdOptions
            root={root}
            model={model}
            question={exportQuestionObj}
            options={exportQuestionOptions}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        ) : (
          <ExportSpecificTurfOptions
            question={exportQuestionObj}
            options={exportQuestionOptions}
            processing={processing}
            disabled={disabled}
            onExport={onExportClick}
          />
        )
      ) : null}
    </div>
  ) : null
}

ExportSpecific.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onExport: PropTypes.func
}

ExportSpecific.displayName = 'ExportSpecific'

export default ExportSpecific
